import { Component, Input, OnInit } from '@angular/core';
import {
  CURRENCIES,
  FUEL_TYPES,
  POWER_TYPES,
  DEFAULT_ENERGY_SAVINGS_DATA,
  FUEL_HEATING_VALUES,
  FUEL_UNITS,
  FUEL_COST_LABELS
} from '../../utils/Constants';
import { TranslateService } from '@ngx-translate/core';
import { ModalController } from '@ionic/angular';
import { StorageService } from 'src/app/services/storage.service';
import { TemperatureService } from 'src/app/services/temperature.service';

export interface EnergyBasicData {
  season: 'winter' | 'summer' | 'both';
  winter?: {
    fuelType: string;
    heatingCost: number;
    heatingCurrency: string;
    calorificValue: number;
    heatingUnit: string;
    heatSeason: number;
    internalTemp: number;
    externalTemp: number;
    efficiency: number;
    costDelta: number;
  };
  summer?: {
    powerType: string;
    electricalCost: number;
    electricalCurrency: string;
    coolSeason: number;
    internalTemp: number;
    externalTemp: number;
    efficiency: number;
    costDelta: number;
  };
}
@Component({
  selector: 'app-basic-data-energy-savings',
  templateUrl: './basic-data-energy-savings.component.html',
  styleUrls: ['./basic-data-energy-savings.component.scss'],
  standalone: false,

})
export class BasicDataEnergySavingsComponent implements OnInit {
  @Input() selectedSeason: 'winter' | 'summer' | 'both' = 'winter';
  temperatureUnit: 'celsius' | 'fahrenheit' = 'celsius';
  isFormValid = false;
  fuelTypes = FUEL_TYPES;
  powerTypes = POWER_TYPES;
  currencies = CURRENCIES;

  // Winter fields
  winterFuelType: string = DEFAULT_ENERGY_SAVINGS_DATA.winter.fuelType;
  winterHeatingCost: number = DEFAULT_ENERGY_SAVINGS_DATA.winter.heatingCost;
  winterCurrency: string = DEFAULT_ENERGY_SAVINGS_DATA.winter.heatingCurrency;
  winterCalorificValue: number = DEFAULT_ENERGY_SAVINGS_DATA.winter.calorificValue;
  winterHeatingUnit: string = DEFAULT_ENERGY_SAVINGS_DATA.winter.heatingUnit;
  winterHeatSeason: number = DEFAULT_ENERGY_SAVINGS_DATA.winter.heatSeason;
  _winterInternalTemp: number = DEFAULT_ENERGY_SAVINGS_DATA.winter.internalTemp;
  _winterExternalTemp: number = DEFAULT_ENERGY_SAVINGS_DATA.winter.externalTemp;
  winterEfficiency: number = DEFAULT_ENERGY_SAVINGS_DATA.winter.efficiency;
  winterCostDelta: number = DEFAULT_ENERGY_SAVINGS_DATA.winter.costDelta;

  // Summer fields
  summerPowerType: string = DEFAULT_ENERGY_SAVINGS_DATA.summer.powerType;
  summerElectricalCost: number = DEFAULT_ENERGY_SAVINGS_DATA.summer.electricalCost;
  summerCurrency: string = DEFAULT_ENERGY_SAVINGS_DATA.summer.electricalCurrency;
  summerCoolSeason: number = DEFAULT_ENERGY_SAVINGS_DATA.summer.coolSeason;
  _summerInternalTemp: number = DEFAULT_ENERGY_SAVINGS_DATA.summer.internalTemp;
  _summerExternalTemp: number = DEFAULT_ENERGY_SAVINGS_DATA.summer.externalTemp;
  summerEfficiency: number = DEFAULT_ENERGY_SAVINGS_DATA.summer.efficiency;
  summerCostDelta: number = DEFAULT_ENERGY_SAVINGS_DATA.summer.costDelta;

  winterCostLabel: string = 'COSTS_LITER';
  constructor(private modalController: ModalController,
    private storageService: StorageService,
    private translate: TranslateService, private tempService: TemperatureService) { }

  async ngOnInit() {
    console.log('Selected season:', this.selectedSeason);
    this.temperatureUnit = await this.tempService.getTemperatureUnit();
    await this.loadSavedData();
    this.updateWinterFuelUnit(this.winterFuelType);
    this.validateForm();
  }

  // Check which fields to show based on season
  get showWinterFields(): boolean {
    return this.selectedSeason === 'winter' || this.selectedSeason === 'both';
  }

  get showSummerFields(): boolean {
    return this.selectedSeason === 'summer' || this.selectedSeason === 'both';
  }

  //  Two-way getters/setters
  get winterInternalTemp(): number {
    return this.temperatureUnit === 'fahrenheit'
      ? this.tempService.celsiusToFahrenheit(this._winterInternalTemp)
      : this._winterInternalTemp;
  }
  set winterInternalTemp(value: number) {
    this._winterInternalTemp = this.temperatureUnit === 'fahrenheit'
      ? this.tempService.fahrenheitToCelsius(value)
      : value;
  }

  get winterExternalTemp(): number {
    return this.temperatureUnit === 'fahrenheit'
      ? this.tempService.celsiusToFahrenheit(this._winterExternalTemp)
      : this._winterExternalTemp;
  }
  set winterExternalTemp(value: number) {
    this._winterExternalTemp = this.temperatureUnit === 'fahrenheit'
      ? this.tempService.fahrenheitToCelsius(value)
      : value;
  }
  //summer
  get summerInternalTemp(): number {
    return this.temperatureUnit === 'fahrenheit'
      ? this.tempService.celsiusToFahrenheit(this._summerInternalTemp)
      : this._summerInternalTemp;
  }
  set summerInternalTemp(value: number) {
    this._summerInternalTemp = this.temperatureUnit === 'fahrenheit'
      ? this.tempService.fahrenheitToCelsius(value)
      : value;
  }

  get summerExternalTemp(): number {
    return this.temperatureUnit === 'fahrenheit'
      ? this.tempService.celsiusToFahrenheit(this._summerExternalTemp)
      : this._summerExternalTemp;
  }
  set summerExternalTemp(value: number) {
    this._summerExternalTemp = this.temperatureUnit === 'fahrenheit'
      ? this.tempService.fahrenheitToCelsius(value)
      : value;
  }

  get tempUnitSymbol(): string {
    return this.temperatureUnit === 'fahrenheit' ? '°F' : '°C';
  }
  // Update calorific value and unit when fuel type changes
  onWinterFuelChange() {
    this.updateWinterFuelUnit(this.winterFuelType);
    this.validateForm();
  }

  updateWinterFuelUnit(fuelType: string) {
    this.winterCalorificValue = FUEL_HEATING_VALUES[fuelType] || 10.00;
    this.winterHeatingUnit = FUEL_UNITS[fuelType] || 'kWh/Ltr.';
    this.winterCostLabel = FUEL_COST_LABELS[fuelType] || 'COSTS_LITER';
  }

  async loadSavedData() {
    try {
      const projectData = await this.storageService.getProject();
      if (projectData) {
        const project = JSON.parse(projectData);
        if (project.energyBasicData) {
          const savedData = project.energyBasicData;

          // Load winter data if exists
          if (savedData.winter) {
            this.winterFuelType = savedData.winter.fuelType || this.winterFuelType;
            this.winterHeatingCost = savedData.winter.heatingCost || this.winterHeatingCost;
            this.winterCurrency = savedData.winter.heatingCurrency || this.winterCurrency;
            this.winterCalorificValue = savedData.winter.calorificValue || this.winterCalorificValue;
            this.winterHeatingUnit = savedData.winter.heatingUnit || this.winterHeatingUnit;
            this.winterHeatSeason = savedData.winter.heatSeason || this.winterHeatSeason;
            this._winterInternalTemp = savedData.winter.internalTemp || this.winterInternalTemp;
            this._winterExternalTemp = savedData.winter.externalTemp || this.winterExternalTemp;
            this.winterEfficiency = savedData.winter.efficiency || this.winterEfficiency;
            this.winterCostDelta = savedData.winter.costDelta || this.winterCostDelta;
          }

          // Load summer data if exists
          if (savedData.summer) {
            this.summerPowerType = savedData.summer.powerType || this.summerPowerType;
            this.summerElectricalCost = savedData.summer.electricalCost || this.summerElectricalCost;
            this.summerCurrency = savedData.summer.electricalCurrency || this.summerCurrency;
            this.summerCoolSeason = savedData.summer.coolSeason || this.summerCoolSeason;
            this._summerInternalTemp = savedData.summer.internalTemp || this.summerInternalTemp;
            this._summerExternalTemp = savedData.summer.externalTemp || this.summerExternalTemp;
            this.summerEfficiency = savedData.summer.efficiency || this.summerEfficiency;
            this.summerCostDelta = savedData.summer.costDelta || this.summerCostDelta;
          }
        }
      }
    } catch (error) {
      console.error('Error loading saved data:', error);
    }
  }

  validateForm(): void {
    let isValid = true;

    if (this.showWinterFields) {
      isValid = isValid &&
        this.winterFuelType !== null &&
        this.winterHeatingCost > 0 &&
        this.winterCurrency !== null &&
        this.winterCalorificValue > 0 &&
        this.winterEfficiency > 0 && this.winterEfficiency <= 100;
    }

    if (this.showSummerFields) {
      isValid = isValid &&
        this.summerPowerType !== null &&
        this.summerElectricalCost > 0 &&
        this.summerCurrency !== null &&
        this.summerEfficiency > 0 && this.summerEfficiency <= 100;
    }

    this.isFormValid = isValid;
  }

  onFieldChange() {
    this.validateForm();
  }

  cancel() {
    this.modalController.dismiss(null, 'cancel');
  }

  async next() {
    if (!this.isFormValid) {
      return;
    }

    // Prepare data based on selected season
    const basicData: EnergyBasicData = {
      season: this.selectedSeason
    };

    if (this.showWinterFields) {
      basicData.winter = {
        fuelType: this.winterFuelType,
        heatingCost: this.winterHeatingCost,
        heatingCurrency: this.winterCurrency,
        calorificValue: this.winterCalorificValue,
        heatingUnit: this.winterHeatingUnit,
        heatSeason: this.winterHeatSeason,
        internalTemp: this._winterInternalTemp,
        externalTemp: this._winterExternalTemp,
        efficiency: this.winterEfficiency,
        costDelta: this.winterCostDelta
      };
    }

    if (this.showSummerFields) {
      basicData.summer = {
        powerType: this.summerPowerType,
        electricalCost: this.summerElectricalCost,
        electricalCurrency: this.summerCurrency,
        coolSeason: this.summerCoolSeason,
        internalTemp: this._summerInternalTemp,
        externalTemp: this._summerExternalTemp,
        efficiency: this.summerEfficiency,
        costDelta: this.summerCostDelta
      };
    }

    // If both seasons, sync the currency and adjust seasons
    if (this.selectedSeason === 'both' && basicData.winter && basicData.summer) {
      basicData.summer.electricalCurrency = basicData.winter.heatingCurrency;
      basicData.summer.coolSeason = 365 - basicData.winter.heatSeason;
    }

    try {
      // Save to storage
      const projectData = await this.storageService.getProject();
      const project = projectData ? JSON.parse(projectData) : {};

      project.energyBasicData = basicData;

      await this.storageService.updateProject({
        energyBasicData: project.energyBasicData
      });

      console.log('Energy basic data saved:', basicData);

      // Dismiss modal with data
      await this.modalController.dismiss(basicData, 'confirm');

    } catch (error) {
      console.error('Error saving basic data:', error);
    }
  }
}
